import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Loader2, Eye, EyeOff, Crosshair } from "lucide-react";
import { api } from "@/lib/api";

const FIELDS = [
  ["manufacturer", "Manufacturer *"], ["model", "Model *"], ["caliber", "Caliber / chambering"],
  ["action", "Action"], ["barrel_length", "Barrel length"], ["overall_length", "Overall length"],
  ["weight", "Weight"], ["capacity", "Capacity"], ["twist_rate", "Twist rate"], ["stock", "Stock"],
  ["optic_mounting", "Optic mounting"], ["msrp", "MSRP"], ["manufacturer_url", "Manufacturer URL"],
  ["seller_url", "Licensed seller URL"], ["image_url", "Image URL"], ["source", "Specs source"],
];
const CATS = ["Rifle", "Shotgun", "Muzzleloader", "Handgun"];

export default function AdminFirearms() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // object or null
  const [showForm, setShowForm] = useState(false);

  const load = () => api.adminFirearms().then((d) => setRows(d.firearms)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ category: "Rifle", status: "draft" }); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setShowForm(true); };

  const togglePublish = async (r) => {
    const status = r.status === "published" ? "draft" : "published";
    try { await api.adminFirearmUpdate(r.id, { status }); toast.success(status === "published" ? "Published" : "Unpublished"); await load(); }
    catch { toast.error("Update failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.manufacturer} ${r.model}?`)) return;
    try { await api.adminFirearmDelete(r.id); toast.success("Deleted"); await load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-firearms">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#1F2A22]">Firearms Research</h1>
          <p className="text-[#5C6B61] text-sm mt-1">Manage the research catalog. Enter verified manufacturer specs — nothing fabricated. HomeRange never sells firearms.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#1F2A22] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#B7761F] transition-colors" data-testid="firearm-new"><Plus size={15} /> Add firearm</button>
      </div>

      {rows === null ? (
        <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>
      ) : rows.length === 0 ? (
        <div className="mt-8 text-center py-16 border border-dashed border-[#D8D3C8]" data-testid="firearms-admin-empty">
          <Crosshair size={30} className="mx-auto text-[#B7761F]" />
          <p className="font-serif text-xl text-[#1F2A22] mt-3">No firearms yet</p>
          <p className="text-[#5C6B61] text-sm mt-1">Add your first model to start the research catalog.</p>
        </div>
      ) : (
        <div className="mt-6 bg-white border border-[#E5E2DC] overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[#5C6B61] border-b border-[#E5E2DC]">
              <th className="p-3 font-semibold">Model</th><th className="p-3 font-semibold">Category</th>
              <th className="p-3 font-semibold">Caliber</th><th className="p-3 font-semibold">Status</th><th className="p-3 font-semibold text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F0EEE8]" data-testid={`firearm-row-${r.id}`}>
                  <td className="p-3 font-medium text-[#1F2A22]">{r.manufacturer} {r.model}</td>
                  <td className="p-3 text-[#5C6B61]">{r.category}</td>
                  <td className="p-3 text-[#5C6B61]">{r.caliber || "—"}</td>
                  <td className="p-3"><span className={`text-[11px] font-semibold uppercase px-2 py-0.5 ${r.status === "published" ? "bg-[#2E7D46]/12 text-[#2E7D46]" : "bg-[#5C6B61]/10 text-[#5C6B61]"}`}>{r.status}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => togglePublish(r)} className="text-[#5C6B61] hover:text-[#B7761F]" title={r.status === "published" ? "Unpublish" : "Publish"} data-testid={`firearm-toggle-${r.id}`}>{r.status === "published" ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                      <button onClick={() => openEdit(r)} className="text-[#5C6B61] hover:text-[#1F2A22]" title="Edit" data-testid={`firearm-edit-${r.id}`}><Pencil size={16} /></button>
                      <button onClick={() => del(r)} className="text-[#5C6B61] hover:text-[#B04A3A]" title="Delete" data-testid={`firearm-del-${r.id}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <FirearmForm initial={editing} onClose={() => setShowForm(false)} onDone={async () => { setShowForm(false); await load(); }} />}
    </div>
  );
}

function FirearmForm({ initial, onClose, onDone }) {
  const [form, setForm] = useState({ category: "Rifle", status: "draft", ...initial });
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.manufacturer?.trim() || !form.model?.trim()) { toast.error("Manufacturer and model are required."); return; }
    setBusy(true);
    try {
      if (isEdit) await api.adminFirearmUpdate(initial.id, form);
      else await api.adminFirearmCreate(form);
      toast.success(isEdit ? "Saved" : "Firearm added");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };

  const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";

  return (
    <div className="fixed inset-0 z-50" data-testid="firearm-form">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-[#F9F8F6] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E2DC] sticky top-0 bg-[#F9F8F6] z-10">
          <h3 className="font-serif text-xl text-[#1F2A22]">{isEdit ? "Edit firearm" : "Add firearm"}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="firearm-form-close"><X size={20} className="text-[#5C6B61]" /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-3">
          <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Category</span>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="firearm-f-category">
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          {FIELDS.map(([k, label]) => (
            <label key={k} className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">{label}</span>
              <input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className={inp} data-testid={`firearm-f-${k}`} /></label>
          ))}
          <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Fit for hunt (applications)</span>
            <textarea rows={2} value={form.hunting_applications || ""} onChange={(e) => set("hunting_applications", e.target.value)} className={inp} data-testid="firearm-f-applications" /></label>
          <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Regulatory notes</span>
            <textarea rows={2} value={form.regulatory_notes || ""} onChange={(e) => set("regulatory_notes", e.target.value)} className={inp} data-testid="firearm-f-regs" /></label>
          <label className="block sm:col-span-2"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Status</span>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inp} data-testid="firearm-f-status">
              <option value="draft">Draft (hidden)</option><option value="published">Published (public)</option>
            </select></label>
        </div>
        <div className="p-5 border-t border-[#E5E2DC] sticky bottom-0 bg-[#F9F8F6]">
          <button onClick={save} disabled={busy} className="w-full bg-[#1F2A22] text-white py-3 text-sm font-semibold hover:bg-[#B7761F] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="firearm-f-save">
            {busy ? <Loader2 size={15} className="animate-spin" /> : null} {isEdit ? "Save changes" : "Add firearm"}
          </button>
        </div>
      </div>
    </div>
  );
}
